import { create } from 'zustand';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  message?: string;
}

interface ToastState {
  toasts: Toast[];
  push: (kind: ToastKind, title: string, message?: string, durationMs?: number) => number;
  dismiss: (id: number) => void;
}

let nextId = 0;
const timers = new Map<number, ReturnType<typeof setTimeout>>();

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push: (kind, title, message, durationMs = 4500) => {
    const id = ++nextId;
    set((state) => ({ toasts: [...state.toasts.slice(-3), { id, kind, title, message }] }));
    if (durationMs > 0) {
      timers.set(
        id,
        setTimeout(() => get().dismiss(id), durationMs),
      );
    }
    return id;
  },
  dismiss: (id) => {
    const timer = timers.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.delete(id);
    }
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }));
  },
}));

export const toast = {
  info: (title: string, message?: string) => useToastStore.getState().push('info', title, message),
  success: (title: string, message?: string) =>
    useToastStore.getState().push('success', title, message),
  warning: (title: string, message?: string) =>
    useToastStore.getState().push('warning', title, message),
  error: (title: string, message?: string) =>
    useToastStore.getState().push('error', title, message, 7000),
};
